<script lang="ts" setup>
  import { inject, useCssModule } from 'vue';

  import { t } from '../../locale';
  import Button from '../Button/Button.vue';
  import { DATA_VIEW_INJECTION } from '../DataView/DataView.vue';
  import Dropdown from '../Dropdown/Dropdown.vue';
  import Icon from '../Icon/Icon.vue';
  import IconLabel from '../IconLabel/IconLabel.vue';
  import type { SortOption } from './DataViewSortButton.types';

  export interface DataViewToolbarProps {
    /**
     * An array of sort options to display in the dropdown.
     */
    sortOptions?: SortOption[];
  }

  const props = withDefaults(defineProps<DataViewToolbarProps>(), {
    sortOptions: () => [],
  });
  const classes = useCssModule();

  const { currentSortId, currentSortOrder, updateCurrentSort } = inject(
    DATA_VIEW_INJECTION.key,
    DATA_VIEW_INJECTION.defaults,
  );
</script>

<template>
  <Dropdown
    v-if="props.sortOptions.length"
    align="left"
    class="stash-data-view-sort-button"
    data-test="stash-data-view-sort-button"
  >
    <template #toggle="{ isActive, toggle }">
      <Button
        icon-label
        :aria-expanded="isActive.toString()"
        class="text-blue-500"
        data-test="stash-data-view-sort-button|sort-menu-button"
        @click="toggle"
      >
        <IconLabel icon="sort" stacked>
          {{ t('ll.sort') }}
        </IconLabel>
      </Button>
    </template>
    <ul class="dropdown__list" data-test="stash-data-view-sort-button|sort-menu">
      <template v-for="sortOption in props.sortOptions" :key="sortOption.id">
        <li
          class="dropdown__item rounded"
          :class="[
            classes.dropdown__item,
            { 'bg-blue-100 text-ice-700': sortOption.id === currentSortId && currentSortOrder === 'asc' },
          ]"
          @click="() => updateCurrentSort(sortOption.id, { sortOrder: 'asc', shouldEmit: true })"
        >
          <Button inline class="h-9">
            {{ sortOption.labelAsc }}
            <Icon
              v-if="sortOption.id === currentSortId && currentSortOrder === 'asc'"
              name="check"
              class="ml-auto text-blue-500"
            />
          </Button>
        </li>
        <li
          class="dropdown__item rounded"
          :class="[
            classes.dropdown__item,
            { 'bg-blue-100 text-ice-700': sortOption.id === currentSortId && currentSortOrder === 'desc' },
          ]"
          @click="() => updateCurrentSort(sortOption.id, { sortOrder: 'desc', shouldEmit: true })"
        >
          <Button inline class="h-9">
            {{ sortOption.labelDesc }}
            <Icon
              v-if="sortOption.id === currentSortId && currentSortOrder === 'desc'"
              name="check"
              class="ml-auto text-blue-500"
            />
          </Button>
        </li>
      </template>
    </ul>
  </Dropdown>
</template>

<style module>
  .dropdown__item > button {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
</style>
